/**
 * Axios 實例 —— 業務 API 請求走這個 client。
 * - request interceptor：若 auth store 有 accessToken，自動塞 Authorization。
 * - response interceptor：401 且不是 /auth/*，嘗試 refresh 後重發原請求；失敗則登出。
 */
import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '../features/auth/store';
import { describeFieldIssue, type FieldLabelMap } from './zod-form-error';

export const apiClient = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// 單 flight refresh：多個並發 401 只觸發一次 refresh。
let refreshPromise: Promise<string | null> | null = null;

type RetryConfig = AxiosRequestConfig & { _retry?: boolean };

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = (error.config ?? {}) as RetryConfig;
    const url = original.url ?? '';

    // 避免對 /auth/* 本身做 refresh（refresh 失敗就失敗）
    if (status === 401 && !original._retry && !url.includes('/auth/')) {
      original._retry = true;
      if (!refreshPromise) {
        refreshPromise = useAuthStore
          .getState()
          .refresh()
          .finally(() => {
            refreshPromise = null;
          });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        original.headers = {
          ...(original.headers ?? {}),
          Authorization: `Bearer ${newToken}`,
        };
        return apiClient(original);
      }
    }

    return Promise.reject(error);
  },
);

/** 後端 `AllExceptionsFilter` 的錯誤格式;頂層 `message` 是舊格式的相容讀取 */
interface ErrorBody {
  error?: { message?: unknown; fields?: unknown };
  message?: unknown;
}

/**
 * 把 API 錯誤轉成給使用者看的中文訊息（REQ-20260803001）。
 *
 * axios 的 `error.message` 是 'Request failed with status code 409' 這種技術訊息；
 * 後端準備好的中文文字放在 `response.data.error.message`（全域 `AllExceptionsFilter`
 * 的格式），這裡負責取出它，取不到才依 status 給通用文案。
 * 任何分支都不回傳 `error.message`。
 *
 * REQ-20260929001:原本讀的是頂層 `data.message`,實際回應裡沒有這個欄位,
 * 所以 400 一律變成「輸入內容有誤」。後端 zod 驗證失敗時另帶 `error.fields`,
 * 這時改指出是哪一格 —— `labels` 由呼叫端傳入,規則同 `describeZodError()`。
 */
export function getApiErrorMessage(
  error: unknown,
  fallback = '系統發生錯誤，請稍後再試。',
  labels: FieldLabelMap = {},
): string {
  if (!axios.isAxiosError(error)) return fallback;

  const data = error.response?.data as ErrorBody | undefined;
  const message =
    describeFields(data?.error?.fields, labels) ??
    readMessage(data?.error?.message) ??
    readMessage(data?.message);
  if (message) return message;

  switch (error.response?.status) {
    case 409:
      return '儲存失敗：資料已存在或發生衝突，請檢查後重試。';
    case 400:
    case 422:
      return '輸入內容有誤，請確認後重試。';
    case 401:
    case 403:
      return '沒有權限執行此操作。';
    default:
      // 含「沒有 response」的情況（逾時、斷線）
      return fallback;
  }
}

function readMessage(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) return value;
  // class-validator 的 400 會回字串陣列
  if (Array.isArray(value)) {
    const parts = value.filter((m): m is string => typeof m === 'string');
    if (parts.length > 0) return parts.join('、');
  }
  return null;
}

/**
 * 後端 zod 驗證失敗時的 `error.fields`(`{ 'lines.0.qty': '必填' }`)→ 指出第一個錯的欄位。
 * key 是 `issue.path.join('.')`,整張表單層級的 issue 是 `'_root'`。
 */
function describeFields(fields: unknown, labels: FieldLabelMap): string | null {
  if (!fields || typeof fields !== 'object') return null;
  const entries = Object.entries(fields);
  const [key, message] = entries[0] ?? [];
  if (key === undefined || typeof message !== 'string') return null;

  // 'lines.0.qty' 的 '0' 要還原成數字,`describeFieldIssue()` 才認得出是第幾筆品項
  const path =
    key === '_root' ? [] : key.split('.').map((seg) => (/^\d+$/.test(seg) ? Number(seg) : seg));
  return describeFieldIssue(path, message, labels, entries.length - 1);
}
